import client from "@/api/client";
import { useInfiniteQuery, useMutation, useQuery } from "@tanstack/react-query"
import { CreateConversationRequestDto, ListMessagesRequestDto, MessageDto, MessageImagePart } from "@/api/sdk";
import { useFlattenInfiniteData } from "@/hooks/useFlattenInfiniteData";
import { useAuth } from "@/providers/authContext";
import { Message } from "@ai-sdk/react";
import { TextUIPart, ReasoningUIPart, ToolInvocationUIPart, SourceUIPart } from "@ai-sdk/ui-utils";

const GET_CONVERSATIONS_MESSAGES_QUERY_KEY = ['conversations', 'messages'];
const CREATE_CONVERSATION_QUERY_KEY = ['create-conversation'];
const GET_CONVERSATION_QUERY_KEY = ['conversation'];

export type ConversationMessage = Message & {
  runId: string;
  replyToMessageId: string;
  images?: MessageImagePart[];
}

/**
 * @description convertMessageDtosToMessages is a function that converts a list of MessageDto objects to a list of Message objects
 * @param messages - a list of MessageDto objects
 * @returns {Message[]} - a list of Message objects
 */
export const convertMessageDtosToMessages = (messages: MessageDto[]): ConversationMessage[] => {
  return messages.reduce((chatMessages: ConversationMessage[], msg) => {
    if (msg.role === "tool") {
      return chatMessages
    }

    // Get the text of the message, if it exists
    const text = msg.content?.find((part) => part.type === "text")?.text ?? msg.text ?? undefined;

    // Get the reasoning of the message, if it exists
    const reasoning = msg.content?.find((part) => part.type === "reasoning")?.reasoning ?? undefined;

    // Get the parts of the message
    const parts = msg.content?.map((part) => {
      if (part.type === "text") {
        return {
          type: "text",
          text: part.text,
        }
      }

      if (part.type === "reasoning") {
        return {
          type: "reasoning",
          reasoning: part.reasoning,
        }
      }

      if (part.type === "tool-invocation") {
        return {
          type: "tool-invocation",
          toolInvocation: part.toolInvocation,
        }
      }

      if (part.type === "source") {
        return {
          type: "source",
          source: part.source,
        }
      }
    })

    chatMessages.push({
      id: msg.id,
      content: text,
      role: msg.role.toLowerCase() as "user" | "assistant" | "system",
      parts: parts as Array<TextUIPart | ReasoningUIPart | ToolInvocationUIPart | SourceUIPart>,
      reasoning: reasoning,
      createdAt: msg.createdAt ? new Date(msg.createdAt) : undefined,
      runId: msg.runId,
      replyToMessageId: msg.replyToMessageId,
      images: msg.images ?? undefined,
    })
    
    return chatMessages
  }, [])
}

/**
 * @description useConversation is a hook that returns a list of messages from a conversation
 * @param conversationId - the id of the conversation to get messages from
 * @returns {Object} - an object containing the messages and a function to get more messages from the conversation
 */
export const useConversation = (conversationId: string | undefined) => {
  const { bearerToken } = useAuth()
  const take = 100; // Maximum number of messages to fetch per page
  const {
    isFetching,
    isSuccess,
    isLoading,
    isError,
    data,
    hasNextPage,
    fetchNextPage,
    refetch,
    error,
  } = useInfiniteQuery({
    queryKey: [...GET_CONVERSATIONS_MESSAGES_QUERY_KEY, conversationId],
    queryFn: ({ pageParam = 0 }) => client.api.aiAgentControllerGetConversationMessages(conversationId!, {
      offset: pageParam * take,
      limit: take
    } as ListMessagesRequestDto).then(res => res.data),
    initialPageParam: 0,
    staleTime: 1000 * 60 * 1,
    getNextPageParam: (lastPage, pages) =>
      lastPage && lastPage.items.length < take ? undefined : pages.length,
    enabled: !!bearerToken && !!conversationId,
    refetchOnWindowFocus: true,
  })

  // Flattern response data
  const [messages, total] = useFlattenInfiniteData(data, {
    flattenFn: (page) => page.items,
    totalFn: (page) => page.total,
  });

  // Revert the array of messages
  const reversedMessages = messages.reverse()

  // Format the messages to be used in the chat
  const formattedMessages = convertMessageDtosToMessages(reversedMessages)

  return {
    messages: formattedMessages,
    total,
    isFetching,
    isSuccess,
    isLoading,
    isError,
    hasNextPage,
    fetchNextPage,
    refetch,
    error,
  }
}

/**
 * @description useCreateConversation is a hook that creates a new conversation
 * @returns {Object} - an object containing the conversation and a function to create a new conversation
 */
export const useCreateConversation = () => {
  const { mutate: createConversation, isPending: isLoading } = useMutation({
    mutationFn: (dto: CreateConversationRequestDto) => 
      client.api.aiAgentControllerCreateConversation(dto).then(res => res.data),
    mutationKey: CREATE_CONVERSATION_QUERY_KEY,
  })

  return {
    createConversation,
    isLoading,
  }
}

/**
 * @description useGetConversation is a hook that gets a conversation by id
 * @param conversationId - the id of the conversation to get
 * @returns {Object} - an object containing the conversation and a function to get a conversation by id
 */
export const useGetConversation = (conversationId: string | undefined) => {
  const { bearerToken } = useAuth()
  const { data, isLoading, isError, refetch, error } = useQuery({
    queryKey: [...GET_CONVERSATION_QUERY_KEY, conversationId],
    queryFn: () => client.api.aiAgentControllerGetConversation(conversationId!).then(res => res.data),
    enabled: !!bearerToken && !!conversationId,
  })

  return {
    conversation: data,
    isLoading,
    isError,
    error,
    refetch,
  }
}

